/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {
  CreateJob,
  DeviceFiles,
  PlatformDeviceTypes,
  PlatformFileTypes
} from '@ion/core/src/types/entities';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {fileTypeUsesAppIds, initializeNewJobTask} from './helpers';
import JobStepMarker from './JobStepMarker';
import JobTasksGrid from './JobTasksGrid';

export interface JobTasksViewProps {
  tasks?: CreateJob;
  setTasks?: (tasks: CreateJob) => void;
  onUpdateTasks?: (tasks: CreateJob) => void;
  tasksInitialized?: boolean;
  deviceTypes?: PlatformDeviceTypes;
  deviceAppIds?: string[];
  accountId?: number;
  includeSubAccounts?: boolean;
  setJobNameUpdateEnabled?: (jobNameUpdateEnabled: boolean) => void;
  isStandalone?: boolean;
  isFileNameMissing?: boolean;
  isDuplicateFile?: boolean;
  isTaskGridEmpty?: boolean;
  setIsTaskGridEmpty?: (isTaskGridEmpty: boolean) => void;
  devicePlatformIds?: Array<Number>;
  isDevicesSelected?: boolean;
  showInfoMsg?: boolean;
  isFetching?: boolean;
  stepper?: boolean;
  hasIMEI2?: boolean;
};

const useStyles = makeStyles(() => ({
  container : {
    margin : ({isStandalone}: {isStandalone: boolean}) => isStandalone ? '-30px 0 0' : '0px'
  },
  errorMessageContainer : {
    padding : '0 0 5px 0'
  },
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '12px',
    fontFamily : 'Roboto',
    fontWeight : 400
  },
  step : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '10px'
  }
}));

/**
 * The JobTasksView provides the UI which allows a user to add new tasks...
 *
 *    import {JobTasksView} from '@ion/applications/src/pages/devices/JobTasksView';
 *
 *    <JobTasksView />
 *
 */
export const JobTasksView = ({
  tasks,
  setTasks,
  onUpdateTasks,
  deviceTypes,
  deviceAppIds,
  accountId,
  includeSubAccounts,
  setJobNameUpdateEnabled,
  setIsTaskGridEmpty,
  isStandalone,
  devicePlatformIds,
  isFetching = false,
  showInfoMsg = false,
  isDevicesSelected = true,
  tasksInitialized = false,
  isFileNameMissing = false,
  isDuplicateFile = false,
  isTaskGridEmpty = false,
  hasIMEI2 = false,
  stepper = false
}: JobTasksViewProps) => {
  const classes           = useStyles({isStandalone});
  const errorTranslations = translate('jobcreate:errors', {returnObjects: true});

  const loadInitialTasks = () => {
    if (deviceTypes?.length && tasksInitialized) {
      if (!tasks?.tasks?.length) {
        const {name, id, fileTypes} = deviceTypes[0];
        const fileType              = fileTypes?.[0];
        const defaultTasks = [{
          id          : '1',
          ancestorIds : [],
          task        : {
            ...initializeNewJobTask(0),
            deviceType   : '',
            fileType     : '',
            deviceTypeId : id,
            fileTypeId   : fileType?.id,
            platformId   : fileType?.platformId,
            usesAppIds   : fileTypeUsesAppIds(fileType),
            deviceAppIds : deviceAppIds || []
          }
        }];
        setTasks({tasks: defaultTasks});
        setIsTaskGridEmpty(false);
      }
    }
  };

  const addTask = useCallback(function(tasks) {
    const task     = tasks?.tasks;
    const newIndex = task?.length;
    const itemId   = Math.max(...task.map(item => Number(item.id))) || '1';
    const newTask  = {
      id          : `${Number(itemId) + Number(1)}`,
      ancestorIds : [],
      task        : {
        ...initializeNewJobTask(newIndex),
        deviceAppIds : deviceAppIds || []
      }
    };
    setTasks({tasks: [...task, newTask]});
    setIsTaskGridEmpty(false);
  }, [deviceAppIds]);

  useEffect(() => {
    loadInitialTasks();
  }, [deviceTypes, tasksInitialized]);

  return (
    <div className={classes.container}>
      <FormSection title={
        <>
          <ConditionalRender condition={!stepper}>
            {translate('jobcreate:tasks')}
          </ConditionalRender>
          <ConditionalRender condition={stepper}>
            <div className={classes.step}>
              <JobStepMarker step={2} disabled={!isDevicesSelected} />
              <div style={{color: dlsColor(`${!isDevicesSelected ? 'grey4' : 'grey1'}`)}}>
                {translate('jobcreate:tasks')}
              </div>
            </div>
          </ConditionalRender>
        </>
      }
      legend={
        <>
          <ConditionalRender condition={stepper}>
            <PrimaryButton onClick={function() { addTask(tasks); } } disabled={!isDevicesSelected}>
              {translate('jobcreate:addTask')}
            </PrimaryButton>
          </ConditionalRender>
          <ConditionalRender condition={!stepper}>
            <TertiaryButton onClick={function() { addTask(tasks); } } disabled={!isDevicesSelected}>
              {translate('jobcreate:addTask')}
            </TertiaryButton>
          </ConditionalRender>
        </>
      }
      children={''} />
      {showInfoMsg &&
        <div className={classes.errorMessageContainer}>
          <span className={classes.errorMessage}>{`${errorTranslations?.jobInfoMsg}`}</span>
        </div>
      }
      {isFileNameMissing &&
        <div className={classes.errorMessageContainer}>
          <span className={classes.errorMessage}>{`${errorTranslations?.fileNameMissing}`}</span>
        </div>
      }
      {isDuplicateFile &&
        <div className={classes.errorMessageContainer}>
          <span className={classes.errorMessage}>{`${errorTranslations?.duplicateFileName}`}</span>
        </div>
      }
      {isTaskGridEmpty &&
        <div className={classes.errorMessageContainer}>
          <span className={classes.errorMessage}>{`${errorTranslations?.noTasks}`}</span>
        </div>
      }
      <ConditionalRender condition={hasIMEI2}>
        <div className={classes.errorMessageContainer}>
          <span className={classes.errorMessage}>{`${errorTranslations?.jobInfoInvalidMsg}`}</span>
        </div>
      </ConditionalRender>
      <JobTasksGrid tasks={tasks}
        isFetching={isFetching}
        setTasks={setTasks}
        onUpdateTasks={onUpdateTasks}
        deviceTypes={deviceTypes}
        deviceAppIds={deviceAppIds}
        accountId={accountId}
        devicePlatformIds={devicePlatformIds}
        isDevicesSelected={isDevicesSelected}
        includeSubAccounts={includeSubAccounts}
        setJobNameUpdateEnabled={setJobNameUpdateEnabled} />
    </div>
  );
};

/**
 * Retrieves a list of files whose type matches the specified type.
 */
export const getFileNames = (
  deviceFiles: DeviceFiles,
  fileType: PlatformFileTypes
): DeviceFiles => {
  let result = [];
  if (deviceFiles?.length && fileType) {
    result = deviceFiles.filter(file =>
      file?.validationMetaData?.platformId === fileType[0]?.platformId);
  };
  return result;
};

/**
 * Retrieves the device type record for the specified device type name.
 */
export const getDeviceType = (
  deviceTypes: PlatformDeviceTypes,
  deviceTypeName: string = 'LMU'
): PlatformDeviceTypes => (
  deviceTypes?.filter(item => item?.name === deviceTypeName) || []
);

export default JobTasksView;
